import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogActions, DialogContent, DialogTitle, TextField, Button,
  CircularProgress, Autocomplete, Box, FormControl, InputLabel, Select, MenuItem, Typography, SelectChangeEvent
} from '@mui/material';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import { apiClient } from '@/services/api';

// 评价规则类型选项
const EVALUATION_RULE_TYPE_OPTIONS = [
  { value: '要素符合度计分', label: '要素符合度计分' },
  { value: '公式计算得分', label: '公式计算得分' },
  { value: '条件判断得分', label: '条件判断得分' },
  { value: '定性与定量结合', label: '定性与定量结合' },
  { value: '递减扣分机制', label: '递减扣分机制' },
  { value: '李克特量表法', label: '李克特量表法' }
];

interface Indicator {
  id?: number;
  level1_name: string;
  level2_name: string;
  indicator_name: string;
  evaluation_rules?: string;
  evaluation_rule_type?: string;
  score?: number;
  evaluation_process_description?: string;
  custom_fields_json?: string;
  weight?: number;
}

interface IndicatorDialogProps {
  open: boolean;
  onClose: () => void;
  indicator?: Indicator | null;
  onSave: (data: any, id?: number) => Promise<void>;
  projectId: string;
  existingIndicators?: Indicator[];
}

const initialFormData = {
  level1_name: '决策',
  level2_name: '',
  indicator_name: '',
  evaluation_rules: '',
  evaluation_rule_type: '要素符合度计分',
  score: 1, // 添加指标时默认为权重值1
  evaluation_process_description: '',
  custom_fields_json: '{}',
  weight: 1,
};

// 防抖Hook
const useDebounce = (value: string, delay: number) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

const IndicatorDialog: React.FC<IndicatorDialogProps> = ({ 
  open, 
  onClose, 
  indicator, 
  onSave, 
  projectId, 
  existingIndicators = [] 
}) => {
  const [formData, setFormData] = useState(initialFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [duplicateNameError, setDuplicateNameError] = useState('');
  const [isGeneratingRule, setIsGeneratingRule] = useState(false);

  // For L3 indicator search
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [isSearchLoading, setIsSearchLoading] = useState(false);

  // For L2 name autocomplete
  const [level2Options, setLevel2Options] = useState<string[]>([]);

  const isEditing = !!indicator;

  useEffect(() => {
    if (isEditing && indicator) {
      const customFields = typeof indicator.custom_fields_json === 'string' 
        ? indicator.custom_fields_json 
        : JSON.stringify(indicator.custom_fields_json || {});
      // 确保所有字段都有默认值，避免undefined
      const safeIndicatorData = {
        ...initialFormData,
        ...indicator,
        custom_fields_json: customFields,
        // 确保关键字段不为undefined
        level1_name: indicator.level1_name || initialFormData.level1_name,
        level2_name: indicator.level2_name || '',
        indicator_name: indicator.indicator_name || '',
        evaluation_rules: indicator.evaluation_rules || '',
        evaluation_rule_type: indicator.evaluation_rule_type || initialFormData.evaluation_rule_type,
        score: indicator.score === null || indicator.score === undefined ? 0 : indicator.score,
        evaluation_process_description: indicator.evaluation_process_description || '',
      };
      setFormData(safeIndicatorData);
    } else {
      setFormData(initialFormData);
    }
    // 清除错误状态
    setDuplicateNameError('');
  }, [indicator, open, isEditing]);

  // Fetch L2 names when L1 name changes
  useEffect(() => {
    if (formData.level1_name) {
      apiClient.get(`/projects/${projectId}/indicators/level2-names?level1=${formData.level1_name}`)
        .then(response => {
          if (response.success) {
            setLevel2Options(response.data?.names || []);
          }
        })
        .catch(console.error);
    }
  }, [formData.level1_name, projectId]);

  // Fetch L3 indicator search results
  useEffect(() => {
    if (debouncedSearchTerm && !isEditing) {
      setIsSearchLoading(true);
      apiClient.get(`/projects/${projectId}/indicators/search?q=${debouncedSearchTerm}`)
        .then(response => {
          if (response.success) {
            // 过滤掉已存在的指标名称
            const existingIndicatorNames = existingIndicators.map(ind => ind.indicator_name);
            const filteredData = (response.data || []).filter((result: any) => 
              !existingIndicatorNames.includes(result.indicator_name)
            );
            setSearchResults(filteredData);
          }
          setIsSearchLoading(false);
        })
        .catch((error) => {
          setIsSearchLoading(false);
        });
    } else {
      setSearchResults([]);
    }
  }, [debouncedSearchTerm, projectId, isEditing, existingIndicators]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    
    // 检查三级指标名称是否重复
    if (name === 'indicator_name') {
      const existingIndicatorNames = existingIndicators.map(ind => ind.indicator_name);
      // 编辑模式下，排除当前正在编辑的指标
      const filteredNames = isEditing 
        ? existingIndicatorNames.filter(name => name !== indicator?.indicator_name)
        : existingIndicatorNames;
      
      if (value && filteredNames.includes(value)) {
        setDuplicateNameError('该指标名称已存在，请选择其他名称');
      } else {
        setDuplicateNameError('');
      }
    }
    
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (e: SelectChangeEvent<string>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };
  
  const handleAutocompleteChange = (name: string, value: string) => {
    // 检查三级指标名称是否重复
    if (name === 'indicator_name') {
      const existingIndicatorNames = existingIndicators.map(ind => ind.indicator_name);
      // 编辑模式下，排除当前正在编辑的指标
      const filteredNames = isEditing 
        ? existingIndicatorNames.filter(name => name !== indicator?.indicator_name)
        : existingIndicatorNames;
      
      if (value && filteredNames.includes(value)) {
        setDuplicateNameError('该指标名称已存在，请选择其他名称');
      } else {
        setDuplicateNameError('');
      }
    }
    
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleL3Select = (_event: any, value: any) => {
    if (typeof value === 'object' && value !== null) {
      // Clean up level1_name by removing prefixes like "A ", "B ", etc.
      const cleanedLevel1Name = (value.level1_name || '').replace(/^[A-Z]\s/, '');
      const validLevel1Names = ['决策', '过程', '产出', '效益'];
      const finalLevel1Name = validLevel1Names.includes(cleanedLevel1Name) ? cleanedLevel1Name : '决策';

      // 检查指标名称是否重复（虽然搜索结果已过滤，但以防万一）
      const existingIndicatorNames = existingIndicators.map(ind => ind.indicator_name);
      // 编辑模式下，排除当前正在编辑的指标
      const filteredNames = isEditing 
        ? existingIndicatorNames.filter(name => name !== indicator?.indicator_name)
        : existingIndicatorNames;
      
      if (value.indicator_name && filteredNames.includes(value.indicator_name)) {
        setDuplicateNameError('该指标名称已存在，请选择其他名称');
      } else {
        setDuplicateNameError('');
      }

      setFormData(prev => ({
        ...prev,
        level1_name: finalLevel1Name,
        level2_name: value.level2_name || '',
        indicator_name: value.indicator_name || '',
        evaluation_rules: value.evaluation_rules || '',
        evaluation_rule_type: value.evaluation_rule_type || '要素符合度计分',
        evaluation_process_description: value.evaluation_process_description || '',
      }));
    } else if (typeof value === 'string') {
      handleAutocompleteChange('indicator_name', value);
    }
  };

  const handleSave = async () => {
    // 最终验证：检查指标名称是否重复
    const existingIndicatorNames = existingIndicators.map(ind => ind.indicator_name);
    // 编辑模式下，排除当前正在编辑的指标
    const filteredNames = isEditing 
      ? existingIndicatorNames.filter(name => name !== indicator?.indicator_name)
      : existingIndicatorNames;
    
    if (filteredNames.includes(formData.indicator_name)) {
      setDuplicateNameError('该指标名称已存在，请选择其他名称');
      return;
    }

    setIsSubmitting(true);
    // Remove properties that shouldn't be sent
    const { id, display_order, indicator_code, ...saveData } = formData as any;

    const dataToSave = {
      ...saveData,
      // 添加指标时使用weight字段，编辑时使用score字段
      weight: isEditing ? undefined : (formData.score || 1),
      score: isEditing ? formData.score : undefined,
    };

    try {
      await onSave(dataToSave, isEditing ? indicator?.id : undefined);
      onClose();
      setDuplicateNameError(''); // 清除错误信息
    } catch (error) {
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAiGenerateRule = async () => {
    if (!formData.indicator_name || !formData.evaluation_rule_type) return;
    try {
      setIsGeneratingRule(true);
      const response = await apiClient.post(`/projects/${projectId}/indicators/generate-rule`, {
        indicator_name: formData.indicator_name,
        level1_name: formData.level1_name,
        level2_name: formData.level2_name,
        evaluation_rule_type: formData.evaluation_rule_type,
      });
      
      if (response.success) {
        setFormData(prev => ({ ...prev, evaluation_rules: response.data.evaluation_rules }));
      }
    } catch (error) {
    } finally {
      setIsGeneratingRule(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h6">{isEditing ? '编辑指标' : '添加新指标'}</Typography>
      </DialogTitle>
      <DialogContent sx={{ paddingTop: '16px !important' }}>

        {!isEditing && (
          <Autocomplete
            freeSolo
            options={searchResults}
            loading={isSearchLoading}
            getOptionLabel={(option) => typeof option === 'string' ? option : option.indicator_name}
            onInputChange={(_e, newInputValue) => setSearchTerm(newInputValue)}
            onChange={handleL3Select}
            renderOption={(props, option) => (
              <li {...props} key={option.indicator_name + option.project_name}>
                <Box>
                  <Typography variant="body1" component="span" fontWeight="fontWeightBold">
                    {option.indicator_name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    层级: {option.level1_name} &gt; {option.level2_name}
                  </Typography>
                </Box>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="输入三级指标名称搜索或直接创建"
                variant="outlined"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {isSearchLoading ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            sx={{ mb: 2 }}
          />
        )}
       
        <FormControl fullWidth sx={{ mb: 2 }} required>
          <InputLabel id="level1-select-label">一级指标名称</InputLabel>
          <Select
            labelId="level1-select-label"
            name="level1_name"
            value={formData.level1_name}
            label="一级指标名称"
            onChange={handleSelectChange}
          >
            {['决策', '过程', '产出', '效益'].map(name => (
              <MenuItem key={name} value={name}>{name}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <Autocomplete
          freeSolo
          options={level2Options}
          value={formData.level2_name}
            onChange={(_event, newValue) => handleAutocompleteChange('level2_name', newValue || '')}
            onInputChange={(_event, newInputValue) => handleAutocompleteChange('level2_name', newInputValue)}
          renderInput={(params) => (
            <TextField 
              {...params} 
              label="二级指标名称" 
              required
            />
          )}
          sx={{ mb: 2 }}
        />

        <TextField
          name="indicator_name"
          label="三级指标名称"
          value={formData.indicator_name}
          onChange={handleInputChange}
          fullWidth
          required
          InputLabelProps={{ shrink: true }}
          sx={{ mb: 2 }}
          error={!!duplicateNameError}
          helperText={duplicateNameError || '指标名称不能与已存在的指标重复'}
        />
        
        <TextField
          name="score"
          label={isEditing ? "得分" : "权重"}
          type="number"
          value={formData.score}
          onChange={handleInputChange}
          fullWidth
          InputLabelProps={{ shrink: true }}
          sx={{ mb: 2 }}
        />
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel id="evaluation-rule-type-label">评价规则类型</InputLabel>
          <Select
            labelId="evaluation-rule-type-label"
            name="evaluation_rule_type"
            value={formData.evaluation_rule_type}
            label="评价规则类型"
            onChange={handleSelectChange}
          >
            {EVALUATION_RULE_TYPE_OPTIONS.map(option => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={isGeneratingRule ? <CircularProgress size={16} /> : <AutoFixHighIcon />}
            disabled={isGeneratingRule || !formData.indicator_name}
            onClick={handleAiGenerateRule}
          >
            AI推荐评价规则
          </Button>
        </Box>

        <TextField
          name="evaluation_rules"
          label="评价规则"
          value={formData.evaluation_rules}
          onChange={handleInputChange}
          fullWidth
          multiline
          rows={4}
          InputLabelProps={{ shrink: true }}
          sx={{ mb: 2 }}
        />
        <TextField
          name="evaluation_process_description"
          label="评价意见"
          value={formData.evaluation_process_description || ''}
          onChange={handleInputChange}
          fullWidth
          multiline
          rows={4}
          InputLabelProps={{ shrink: true }}
          sx={{ mb: 2 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>取消</Button>
        <Button 
          onClick={handleSave} 
          variant="contained" 
          disabled={isSubmitting || !!duplicateNameError}
        >
          {isSubmitting ? <CircularProgress size={24} /> : (isEditing ? '保存更改' : '创建指标')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default IndicatorDialog;
